import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { adminApi, errMsg, fileUrl, fmt } from "@/lib/api";

const EMPTY = { title: "", description: "", type: "loss", period: "weekly", percent: 10, min_amount: 0, max_amount: 0, banner_path: null, active: true, order: 0 };

const BonusForm = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [up, setUp] = useState(false);
  const ref = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/upload", fd);
      setF((x) => ({ ...x, banner_path: data.path }));
    } catch (err) {
      toast.error(errMsg(err));
    }
    setUp(false);
  };

  const save = async () => {
    try {
      const body = { ...f, percent: Number(f.percent), min_amount: Number(f.min_amount), max_amount: Number(f.max_amount), order: Number(f.order) };
      delete body.id; delete body.created_at;
      const { data } = initial.id ? await adminApi.put(`/admin/bonuses/${initial.id}`, body) : await adminApi.post("/admin/bonuses", body);
      toast.success("Bonus kaydedildi");
      onSaved(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="x-panel p-5 space-y-4" data-testid="bonus-form">
      <button type="button" onClick={() => ref.current?.click()} className="relative w-full aspect-[16/6] rounded-lg overflow-hidden bg-[var(--x-bg-deep)] border-2 border-dashed border-[var(--x-border)] flex items-center justify-center" data-testid="bonus-banner-upload-btn">
        {f.banner_path ? <img src={fileUrl(f.banner_path)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : (
          <span className="flex items-center gap-2 text-[var(--x-muted)] font-bold">{up ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />} Banner Yükle</span>
        )}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><label className="x-label">Başlık</label><input data-testid="bonus-title-input" className="x-input" value={f.title} onChange={set("title")} /></div>
        <div className="sm:col-span-2"><label className="x-label">Açıklama</label><textarea data-testid="bonus-desc-input" rows={2} className="x-input" value={f.description} onChange={set("description")} /></div>
        <div>
          <label className="x-label">Bonus Türü</label>
          <select data-testid="bonus-type-select" className="x-input" value={f.type} onChange={set("type")}>
            <option value="loss">Kayıp Bonusu (net kayıp üzerinden)</option>
            <option value="deposit">Yatırım Bonusu (onaylı yatırım üzerinden)</option>
          </select>
        </div>
        <div>
          <label className="x-label">Dönem</label>
          <select data-testid="bonus-period-select" className="x-input" value={f.period} onChange={set("period")}>
            <option value="weekly">Haftalık</option>
            <option value="monthly">Aylık</option>
          </select>
        </div>
        <div><label className="x-label">Yüzde (%)</label><input data-testid="bonus-percent-input" type="number" className="x-input" value={f.percent} onChange={set("percent")} /></div>
        <div><label className="x-label">Sıra</label><input type="number" className="x-input" value={f.order} onChange={set("order")} /></div>
        <div><label className="x-label">Min. Tutar (₺)</label><input data-testid="bonus-min-input" type="number" className="x-input" value={f.min_amount} onChange={set("min_amount")} /></div>
        <div><label className="x-label">Maks. Bonus (₺, 0 = sınırsız)</label><input data-testid="bonus-max-input" type="number" className="x-input" value={f.max_amount} onChange={set("max_amount")} /></div>
      </div>
      <div className="flex items-center justify-between"><span className="font-bold">Aktif</span><Switch data-testid="bonus-active-switch" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></div>
      <div className="flex gap-2">
        <button data-testid="bonus-save-btn" onClick={save} className="x-btn x-btn-primary flex-1">Kaydet</button>
        <button onClick={onCancel} className="x-btn x-btn-secondary">İptal</button>
      </div>
    </div>
  );
};

export const AdminBonuses = () => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => adminApi.get("/admin/bonuses").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);
  const del = async (id) => {
    if (!window.confirm("Bonus silinsin mi?")) return;
    await adminApi.delete(`/admin/bonuses/${id}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-[1fr_420px] gap-5">
      <div className="space-y-3">
        <button data-testid="bonus-new-btn" onClick={() => setEdit({ ...EMPTY })} className="x-btn x-btn-primary"><Plus size={16} /> Yeni Bonus</button>
        {list.map((b) => (
          <div key={b.id} className="x-panel p-4 flex gap-4 items-center" data-testid={`admin-bonus-${b.id}`}>
            <div className="w-28 aspect-[16/9] rounded-md overflow-hidden bg-gradient-to-br from-[#1475e1] to-[#0b3f80] shrink-0">
              {b.banner_path && <img src={fileUrl(b.banner_path)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-extrabold truncate">{b.title} {!b.active && <span className="x-chip bg-white/10 text-[var(--x-muted)]">Pasif</span>}</div>
              <div className="text-[13px] text-[var(--x-muted)]">
                {b.type === "loss" ? "Kayıp" : "Yatırım"} · {b.period === "weekly" ? "Haftalık" : "Aylık"} · %{fmt(b.percent, 0)}{b.max_amount ? ` · maks ₺${fmt(b.max_amount, 0)}` : ""}
              </div>
            </div>
            <button onClick={() => setEdit(b)} className="x-btn x-btn-secondary px-3 py-1.5 text-[13px]">Düzenle</button>
            <button onClick={() => del(b.id)} className="x-btn x-btn-red px-3 py-1.5 text-[13px]">Sil</button>
          </div>
        ))}
        {list.length === 0 && <div className="x-panel p-6 text-center text-[var(--x-dim)]">Henüz bonus yok</div>}
      </div>
      {edit && <BonusForm key={edit.id || "new"} initial={edit} onCancel={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};
